// El estado del plan de una sesión de Videos cortos y TODAS sus acciones
// (ADR-2026-10-30-videos-cortos-con-el-agente). Son las mismas para la pantalla, el panel del video
// y el agente (sus herramientas llaman acá: nunca un setState que saltee estas reglas). Cada cambio
// pasa por `setPlan` (el ref se actualiza SINCRÓNICAMENTE, como en el Catálogo: las herramientas
// del agente encadenan acciones y leen el plan entre medio) y `usePlanDeVideosGuardado` lo persiste.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { VozDeVideo } from './videosCortosApi';
import { escribirGuiones, type ResultadoDeEscritura } from './escritorDeGuiones';
import { generarVideos, seguirLotes, type ResultadoDeGeneracion } from './generarVideos';
import {
  desfijar as desfijarEnPlan,
  editarGuion as editarGuionEnPlan,
  lotesAbiertos,
  marcarRehacer as marcarRehacerEnPlan,
  productosPendientesDeEscritura,
  quitarProductos as quitarProductosDelPlan,
  quitarRehacer as quitarRehacerEnPlan,
  setIndicacion as setIndicacionEnPlan,
  setNotas as setNotasEnPlan,
  setSubtitulos as setSubtitulosEnPlan,
  setTitulo as setTituloEnPlan,
  setVoz as setVozEnPlan,
  sumarProductos as sumarProductosAlPlan,
  type FiltroDeProductos,
  type PlanDeVideos,
  type ProductoParaLaTanda,
  type ProductosSumados,
} from './plan';
import { guardarPlanDeVideos, leerPlanDeVideos, usePlanDeVideosGuardado } from './planGuardado';

export type CambioDePlanDeVideos = PlanDeVideos | ((prev: PlanDeVideos) => PlanDeVideos);

export interface AccionesDeLaTanda {
  setTitulo: (t: string) => void;
  setIndicacion: (i: string) => void;
  setSubtitulos: (s: boolean) => void;
  /** La voz de un producto (`varianteId`) o de la tanda (`null`). */
  setVoz: (varianteId: string | null, voz: VozDeVideo | null) => void;
  setNotas: (varianteId: string, notas: string) => void;
  /** Deja el prompt y/o la locución y FIJA el guion: el escritor no lo pisa. */
  editarGuion: (varianteId: string, parcial: { prompt?: string; locucion?: string }) => void;
  desfijar: (varianteId: string) => void;
  /** Escribe con el escritor del backend los productos pendientes (o los de `solo`). */
  escribir: (solo?: FiltroDeProductos, indicacion?: string, aunqueEstenEscritos?: boolean) => Promise<ResultadoDeEscritura>;
  marcarRehacer: (ids?: string[], o?: { soloVoz?: boolean }) => { marcados: string[] };
  quitarRehacer: (ids?: string[]) => void;
  sumarProductos: (productos: ProductoParaLaTanda[]) => ProductosSumados;
  quitarProductos: (ids: string[]) => { quitados: string[]; enVuelo: string[] };
  /** Crea los videos por lanzar (o `soloIds`): un lote nuevo. */
  generar: (soloIds?: string[]) => Promise<ResultadoDeGeneracion>;
  /** Registra que el agente pidió crear: espera al click en Crear (o a "No crear"). */
  pedirGenerar: () => Promise<ResultadoDeGeneracion>;
  cancelarGenerar: () => void;
  abrirProducto: (id: string | null) => void;
}

export interface PlanDeVideosEnSesion {
  plan: PlanDeVideos | null;
  setPlan: (p: CambioDePlanDeVideos) => void;
  cargando: boolean;
  noExiste: boolean;
  escribiendo: boolean;
  generando: boolean;
  generarPedido: boolean;
  productoAbierto: string | null;
  acciones: AccionesDeLaTanda;
  /** El plan al instante, con lo que las acciones de este mismo tick ya cambiaron. */
  leerPlan: () => PlanDeVideos | null;
}

export function usePlanDeVideos(p: { chatId: string | undefined; companyId: string | null; planInicial?: PlanDeVideos | null }): PlanDeVideosEnSesion {
  const [plan, setPlanState] = useState<PlanDeVideos | null>(p.planInicial ?? null);
  const [cargando, setCargando] = useState(!p.planInicial);
  const [noExiste, setNoExiste] = useState(false);
  const [escribiendo, setEscribiendo] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [generarPedido, setGenerarPedido] = useState(false);
  const [productoAbierto, setProductoAbierto] = useState<string | null>(null);
  const planRef = useRef<PlanDeVideos | null>(p.planInicial ?? null);
  planRef.current = plan;
  const resolverGenerar = useRef<((r: ResultadoDeGeneracion) => void) | null>(null);
  const vivo = useRef(true);
  const salida = useRef(new AbortController());

  const setPlan = useCallback((v: CambioDePlanDeVideos) => {
    const siguiente = typeof v === 'function' ? (planRef.current ? v(planRef.current) : null) : v;
    if (!siguiente) return;
    planRef.current = siguiente;
    setPlanState(siguiente);
  }, []);

  usePlanDeVideosGuardado(p.chatId, plan);

  // ── Seguir los lotes abiertos (al cargar y después de cada Crear) ──────────
  const { chatId, companyId } = p;
  const siguiendo = useRef(false);
  const seguir = useCallback(() => {
    if (siguiendo.current || !companyId) return;
    const actual = planRef.current;
    if (!actual || lotesAbiertos(actual).length === 0) return;
    siguiendo.current = true;
    void seguirLotes({ leerPlan: () => planRef.current, companyId, onPlan: (pl) => vivo.current && setPlan(pl), signal: salida.current.signal }).finally(() => {
      siguiendo.current = false;
    });
  }, [companyId, setPlan]);

  // ── Cargar ─────────────────────────────────────────────────────────────────
  const traiaPlan = useRef(!!p.planInicial);
  useEffect(() => {
    vivo.current = true;
    if (salida.current.signal.aborted) salida.current = new AbortController();
    const alSalir = () => {
      vivo.current = false;
      salida.current.abort();
    };
    if (!chatId) return alSalir;
    if (traiaPlan.current) {
      traiaPlan.current = false;
      return alSalir;
    }
    setCargando(true);
    setNoExiste(false);
    void leerPlanDeVideos(chatId).then((leido) => {
      if (!vivo.current) return;
      if (!leido) {
        setNoExiste(true);
        setCargando(false);
        return;
      }
      setPlan(leido);
      setCargando(false);
      seguir();
    });
    return alSalir;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);
  // Con la empresa ya resuelta, un plan que vino de la portada o que se cargó antes también se sigue.
  useEffect(() => {
    seguir();
  }, [seguir, plan?.lotes.length]);

  const acciones = useMemo<AccionesDeLaTanda>(
    () => ({
      setTitulo: (t) => setPlan((prev) => setTituloEnPlan(prev, t)),
      setIndicacion: (i) => setPlan((prev) => setIndicacionEnPlan(prev, i)),
      setSubtitulos: (s) => setPlan((prev) => setSubtitulosEnPlan(prev, s)),
      setVoz: (varianteId, voz) => setPlan((prev) => setVozEnPlan(prev, varianteId, voz)),
      setNotas: (varianteId, notas) => setPlan((prev) => setNotasEnPlan(prev, varianteId, notas)),
      editarGuion: (varianteId, parcial) => setPlan((prev) => editarGuionEnPlan(prev, varianteId, parcial)),
      desfijar: (varianteId) => setPlan((prev) => desfijarEnPlan(prev, varianteId)),
      escribir: async (solo, indicacion, aunqueEstenEscritos) => {
        const actual = planRef.current;
        if (!actual) return { plan: actual as unknown as PlanDeVideos, escritos: 0, fallidos: [] };
        const productos = productosPendientesDeEscritura(actual, solo, aunqueEstenEscritos);
        if (productos.length === 0) return { plan: actual, escritos: 0, fallidos: [] };
        const pedidos = new Set(productos.map((x) => x.varianteId));
        setEscribiendo(true);
        try {
          const r = await escribirGuiones({
            plan: actual,
            productos,
            indicacion,
            signal: salida.current.signal,
            // Lo que llega del escritor se funde sobre lo que la persona pudo tocar mientras tanto: cada guion se
            // vuelca sobre el plan vivo por producto.
            onProducto: (pl) => {
              if (!vivo.current) return;
              setPlan((prev) => ({
                ...prev,
                productos: prev.productos.map((pr) => {
                  // Sólo los que se mandaron a escribir: la copia del escritor tiene a todos, y copiar a los demás
                  // devolvía a "escrito" un video que terminó mientras tanto (Greptile #233).
                  if (!pedidos.has(pr.varianteId)) return pr;
                  const l = pl.productos.find((x) => x.varianteId === pr.varianteId);
                  if (!l || pr.fijado || pr.estado === 'en_cola' || pr.estado === 'generando') return pr;
                  return { ...pr, guion: l.guion, estado: l.estado, motivo: l.motivo };
                }),
              }));
            },
          });
          return { ...r, plan: planRef.current ?? r.plan };
        } finally {
          if (vivo.current) setEscribiendo(false);
        }
      },
      marcarRehacer: (ids, o) => {
        let r = { marcados: [] as string[] };
        setPlan((prev) => {
          const m = marcarRehacerEnPlan(prev, ids, o);
          r = { marcados: m.marcados };
          return m.plan;
        });
        return r;
      },
      quitarRehacer: (ids) => setPlan((prev) => quitarRehacerEnPlan(prev, ids)),
      sumarProductos: (productos) => {
        const actual = planRef.current;
        if (!actual) return { plan: actual as unknown as PlanDeVideos, sumados: [], yaEstaban: [], sinLugar: productos.map((x) => x.varianteId) };
        const r = sumarProductosAlPlan(actual, productos);
        if (r.sumados.length > 0) setPlan(r.plan);
        return r;
      },
      quitarProductos: (ids) => {
        const actual = planRef.current;
        if (!actual) return { quitados: [], enVuelo: [] };
        const r = quitarProductosDelPlan(actual, ids);
        if (r.quitados.length > 0) setPlan(r.plan);
        return { quitados: r.quitados, enVuelo: r.enVuelo };
      },
      generar: async (soloIds) => {
        const actual = planRef.current;
        if (!actual || !companyId || !chatId) {
          return { ok: false, error: 'La sesión no está lista.', sinCreditos: false, plan: actual as unknown as PlanDeVideos, lanzados: 0 };
        }
        setGenerando(true);
        setGenerarPedido(false);
        try {
          const r = await generarVideos({
            plan: actual,
            soloIds,
            onPlan: (pl) => vivo.current && setPlan(pl),
            guardarYa: (pl) => guardarPlanDeVideos(chatId, pl),
            leerPlan: () => planRef.current,
          });
          resolverGenerar.current?.(r);
          resolverGenerar.current = null;
          seguir();
          return r;
        } finally {
          if (vivo.current) setGenerando(false);
        }
      },
      pedirGenerar: () => {
        if (resolverGenerar.current) {
          return Promise.resolve({ ok: false as const, error: 'ya hay un pedido de crear esperando el click', sinCreditos: false, plan: planRef.current!, lanzados: 0 });
        }
        return new Promise<ResultadoDeGeneracion>((resolve) => {
          resolverGenerar.current = resolve;
          setGenerarPedido(true);
        });
      },
      cancelarGenerar: () => {
        const r = resolverGenerar.current;
        resolverGenerar.current = null;
        setGenerarPedido(false);
        r?.({ ok: false, error: 'la persona no quiso crear los videos todavía', sinCreditos: false, plan: planRef.current!, lanzados: 0 });
      },
      abrirProducto: setProductoAbierto,
    }),
    [setPlan, companyId, chatId, seguir],
  );

  const leerPlanVivo = useCallback(() => planRef.current, []);
  return { plan, setPlan, cargando, noExiste, escribiendo, generando, generarPedido, productoAbierto, acciones, leerPlan: leerPlanVivo };
}
